import { initializeApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported as isFirebaseSdkSupported,
} from 'firebase/messaging';

import { addListener } from 'lib/eventManager';

import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setLocalStorage,
} from './localStorage';
import { post } from './request';
import {
  getFromSessionStorage,
  removeFromSessionStorage,
  setSessionStorage,
} from './sessionStorage';
import { isSafariAgent, isServiceWorkersSupported } from './userAgent';
import { isDev, log as logger } from './utils';

import FIREBASE_CONFIG from 'constants/firebase';
import LOCAL_STORAGE from 'constants/localStorage';
import USER from 'constants/user';

export const isNotificationOnboardingShowed = getFromLocalStorage(
  LOCAL_STORAGE.SHOWED_WELCOME_NOTIFICATION
);

const isSessionWebpushNotificationAllowed =
  !!JSON.parse(getFromSessionStorage('me.webPushNotification')) ?? false;

const log = (...args) => logger('webpush', ...args);

export const clearStoragesWebPushPermissionEntries = async () => {
  try {
    removeFromSessionStorage('me.webPushNotification');
    removeFromLocalStorage(LOCAL_STORAGE.SHOWED_WELCOME_NOTIFICATION);
    removeFromLocalStorage(LOCAL_STORAGE.WEBPUSH_HASH);

    if (isServiceWorkersSupported()) {
      const registrations = await navigator.serviceWorker.getRegistrations();

      registrations.forEach((registration) => {
        const isFirebaseSWActive = registration?.active?.scriptURL.includes(
          '/dist/firebase-messaging-sw.js'
        );

        if (isFirebaseSWActive) {
          registration.unregister();
        }
      });
    }
  } catch {
    // ignore errors of third party cookies are not allowed in client side
  }
};

export const initializeFirebaseApp = async () => {
  if (!isFirebaseSdkSupported()) {
    return;
  }

  initializeApp(FIREBASE_CONFIG);

  /**
   *  Safari adds WebpushAPI support only in your version launched in October of
   * 2022. Because this are a young implementation and are few adopters, we
   * don't initialize the webpush with Safari.
   */
  if (!isServiceWorkersSupported() && !isSafariAgent()) {
    return;
  }

  await subscribeToServiceWorker();
};

const startNotificationOnboarding = async (registration) => {
  setLocalStorage(LOCAL_STORAGE.SHOWED_WELCOME_NOTIFICATION, true);
  await registration.showNotification('Configure sua Lista de Desejos', {
    body: 'Adicione produtos que você está procurando e receba alertas sempre que aparecer no Promobit.',
    icon: 'https://www.promobit.com.br/images/general/icons/genie/genio_webpush.png',
  });
  await new Promise((resolve) => setTimeout(resolve, 2000));
  await registration.showNotification('Notificações Prontas', {
    body: 'Você receberá alertas neste dispositivo.',
    icon: 'https://www.promobit.com.br/images/general/icons/genie/genio_webpush.png',
  });
};

const subscribeToServiceWorker = async () => {
  const messaging = getMessaging();

  const getSupixUserHash = async () => {
    if (isDev()) {
      return { user_hash: '' };
    }

    const req = await fetch(`${process.env.NEXT_PUBLIC_SUPIX_URL}/Service/h`);
    return req.json();
  };

  const { user_hash: webpushHash } = await getSupixUserHash();

  const sendSubscriptionToServer = ({ token, isLoggedIn = false }) => {
    const postSubscription = () =>
      post('/webpush', {
        body: {
          userAuthToken: token,
          webpushHash:
            getFromLocalStorage(LOCAL_STORAGE.WEBPUSH_HASH) ?? webpushHash,
        },
      });

    if (isLoggedIn) {
      addListener(USER.EVENTS.LOGIN_CHECKED, postSubscription, {
        runLostEmits: true,
      });
      return;
    }

    postSubscription();
  };

  try {
    const serviceWorkerRegistration = await navigator.serviceWorker.register(
      '/dist/firebase-messaging-sw.js'
    );

    const token = await getToken(messaging, {
      serviceWorkerRegistration,
      vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
    });

    if (isDev()) {
      log(`Firebase generates this FCM token:\n ${token}`);
    }

    if (token) {
      if (!isSessionWebpushNotificationAllowed) {
        startNotificationOnboarding(serviceWorkerRegistration);
      }
      if (
        !getFromLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN) &&
        !isSessionWebpushNotificationAllowed
      ) {
        setSessionStorage('me.webPushNotification', '{"webpush":true}');
        setLocalStorage(LOCAL_STORAGE.WEBPUSH_HASH, webpushHash);
        sendSubscriptionToServer({ token });
        return;
      }
      if (getFromLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN)) {
        sendSubscriptionToServer({ token, isLoggedIn: true });
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Service Worker registration failed: ', error);
  }
};
